/**
 * Shared pieces of the sign-in and onboarding forms: email handling, the
 * validation copy every form uses, and moving focus to the first problem.
 */

/**
 * Anchored: the old /\S+@\S+\.\S+/ matched anywhere in the string, so
 * "a b@c.d" or "x@y.z junk" passed and Firebase rejected them instead.
 */
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Account emails are stored lower-cased and trimmed. Sign-in, sign-up, the
 * rate-limit key, monitoring and resend all use this form, so " User@Example.com"
 * (an autofilled trailing space, a capitalised first letter on mobile) works
 * instead of failing as an invalid email or counting as a different address.
 */
export const normalizeEmail = (value: string): string => value.trim().toLowerCase();

export const SUPPORT_EMAIL = 'info@artbits.solutions';
export const SUPPORT_HREF = `mailto:${SUPPORT_EMAIL}`;

/** Validation copy shared by every auth form, so the same problem reads the same way everywhere. */
export const FIELD_ERRORS = {
  emailRequired: 'Enter your email address.',
  emailInvalid: 'Enter a valid email address.',
  passwordRequired: 'Enter your password.',
  newPasswordRequired: 'Choose a password.',
  newPasswordWeak: 'Your password needs to meet every requirement below.',
  confirmRequired: 'Enter your password again.',
  confirmMismatch: "Passwords don't match.",
  nameRequired: 'Enter your name.',
} as const;

/** The email problem to show under the field, or undefined when it is fine. */
export const validateEmail = (value: string): string | undefined => {
  const normalized = normalizeEmail(value);
  if (!normalized) return FIELD_ERRORS.emailRequired;
  if (!EMAIL_PATTERN.test(normalized)) return FIELD_ERRORS.emailInvalid;
  return undefined;
};

export type FieldErrors<K extends string> = Partial<Record<K, string>>;

/**
 * Moves keyboard focus to the first field (in visual order) that has an
 * error, so a failed submit puts the cursor where the fix is.
 */
export const focusFirstInvalid = <K extends string>(
  order: readonly K[],
  errors: FieldErrors<K>,
  refs: Partial<Record<K, { current: HTMLElement | null }>>
): void => {
  const first = order.find((key) => Boolean(errors[key]));
  if (first) refs[first]?.current?.focus();
};
